import { VIRTUAL_WIDTH, VIRTUAL_HEIGHT } from '../../config/constants.js';
import { isMobileModeActive } from './deviceMode.js';

/**
 * Owns the low-res "virtual" backbuffer that all game rendering draws into,
 * then blits it to the real, screen-sized canvas with nearest-neighbor
 * scaling. This is what gives everything the crisp, blocky pixel-art look
 * regardless of the player's actual window/monitor resolution.
 *
 * Also owns a high-res text overlay canvas that sits on top of the game
 * canvas, so HUD text renders crisply at the display's native resolution
 * instead of being nearest-neighbor scaled from the low-res buffer.
 */
export class Renderer {
  constructor(displayCanvas, save) {
    this.displayCanvas = displayCanvas;
    this.save = save;
    this.displayCtx = displayCanvas.getContext('2d');
    this.displayCtx.imageSmoothingEnabled = false;

    this.buffer = document.createElement('canvas');
    this.buffer.width = VIRTUAL_WIDTH;
    this.buffer.height = VIRTUAL_HEIGHT;
    this.ctx = this.buffer.getContext('2d');
    this.ctx.imageSmoothingEnabled = false;

    this.textCanvas = document.createElement('canvas');
    this.textCanvas.className = 'text-overlay';
    displayCanvas.parentNode.insertBefore(this.textCanvas, displayCanvas.nextSibling);
    this.textCtx = this.textCanvas.getContext('2d');

    this._resize();
    // Bound (not an inline arrow) so destroy() can remove this exact
    // listener — otherwise every Renderer ever created (one per run, see
    // Game.js's constructor) leaks both this listener and a live reference
    // keeping it (and its canvases) from being garbage collected.
    this._onResize = () => this._resize();
    window.addEventListener('resize', this._onResize);
    // #app's CSS (style.css) shrinks to a safe frame — away from browser
    // chrome/OS taskbar — while windowed, and expands to fill the screen on
    // :fullscreen; that's a CSS-only change the 'resize' event doesn't
    // always follow, so the HUD needs its own nudge to stay in sync (same
    // event MainMenu.js already listens to for its fullscreen icon).
    document.addEventListener('fullscreenchange', this._onResize);
  }

  /** Removes the DOM canvas + window listener this Renderer added — call when its owning Game is destroyed. */
  destroy() {
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('fullscreenchange', this._onResize);
    this.textCanvas.remove();
  }

  _resize() {
    const dpr = window.devicePixelRatio || 1;
    // Measure the canvas's own CSS box (driven by #app in style.css) rather
    // than window.innerWidth/innerHeight: the window's raw inner size can
    // include space that's actually covered by browser chrome/OS taskbar,
    // while #app's dvh/svh-based sizing already carves out a safe frame for
    // exactly that case. Sizing off window dimensions instead of the box
    // that's actually visible was why the HUD (health/exp/boss bars) could
    // end up drawn partly behind that chrome while not fullscreen.
    const rect = this.displayCanvas.getBoundingClientRect();
    this.displayCanvas.width = Math.floor(rect.width * dpr);
    this.displayCanvas.height = Math.floor(rect.height * dpr);
    this.displayCtx.imageSmoothingEnabled = false;

    this.textCanvas.width = Math.floor(rect.width * dpr);
    this.textCanvas.height = Math.floor(rect.height * dpr);
    this.textCtx.imageSmoothingEnabled = true;

    const scaleX = this.displayCanvas.width / VIRTUAL_WIDTH;
    const scaleY = this.displayCanvas.height / VIRTUAL_HEIGHT;
    // Mobile mode (see core/deviceMode.js — hardware auto-detect, or the
    // player's explicit Mobile Controls choice) uses "contain"/letterbox
    // (min): the virtual buffer is a fixed 16:9-ish frame (384x216), and
    // every phone in landscape is noticeably wider than that (e.g. ~2.16:1
    // on an iPhone) — "cover" would crop the narrower axis to fill the
    // screen, silently hiding bullets/HUD near that edge in a game where
    // seeing incoming attacks is safety-critical. Letterboxing spends
    // unused screen space as black bars instead, which is the right trade
    // specifically there.
    //
    // Everyone else keeps "cover" (max): laptop/monitor aspect ratios
    // (16:10, 3:2, ultrawide, ...) are close enough to 16:9 that the crop
    // is minor, and that's a better trade than the black bars "contain"
    // would leave on those.
    const isMobile = isMobileModeActive(this.save);
    // Continuous, not floored to a whole number — flooring (e.g. 4.96x -> 4x)
    // kept pixel edges perfectly crisp, but only at window sizes that happen
    // to land near an exact integer multiple of the 384x216 buffer, which a
    // maximized/fullscreen window on a native-resolution monitor often does
    // and an arbitrary windowed browser size almost never does. That made
    // the game look tiny (a whole unused scale step of black bars) anywhere
    // short of fullscreen. A fractional nearest-neighbor stretch has a
    // faint, rarely-noticeable unevenness to it; that's a better trade than
    // routinely wasting up to ~20% of the window.
    this.scale = isMobile ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY);

    this.offsetX = (this.displayCanvas.width - VIRTUAL_WIDTH * this.scale) / 2;
    this.offsetY = (this.displayCanvas.height - VIRTUAL_HEIGHT * this.scale) / 2;
  }

  /**
   * The virtual-space rect actually on screen after cover/contain scaling.
   * The 384x216 buffer is exactly 16:9, so "cover" (desktop) crops symmetric
   * top/bottom (or left/right) strips whenever the canvas box isn't exactly
   * 16:9 — which is every windowed browser with chrome. Screen-edge HUD
   * (HP/XP bars, boss bar, telegraphs, run clock) must anchor to these
   * bounds, not raw VIRTUAL_WIDTH/HEIGHT, or it lands in the cropped margin
   * and silently disappears. Clamped to the virtual frame so the letterboxed
   * (contain/mobile) case keeps HUD inside the game image, not the bars.
   */
  visibleVirtualBounds() {
    const left = Math.max(0, -this.offsetX / this.scale);
    const top = Math.max(0, -this.offsetY / this.scale);
    const right = Math.min(VIRTUAL_WIDTH, (this.displayCanvas.width - this.offsetX) / this.scale);
    const bottom = Math.min(VIRTUAL_HEIGHT, (this.displayCanvas.height - this.offsetY) / this.scale);
    return { left, top, right, bottom, centerX: (left + right) / 2 };
  }

  /** Clears the virtual backbuffer; call at the start of each frame. */
  beginFrame() {
    this.ctx.fillStyle = '#0a0a12';
    this.ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
    this.textCtx.clearRect(0, 0, this.textCanvas.width, this.textCanvas.height);
  }

  /** Blits the virtual backbuffer to the screen canvas, letterboxed + scaled. */
  present() {
    const d = this.displayCtx;
    d.fillStyle = '#000000';
    d.fillRect(0, 0, this.displayCanvas.width, this.displayCanvas.height);
    d.drawImage(
      this.buffer,
      0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT,
      this.offsetX, this.offsetY, VIRTUAL_WIDTH * this.scale, VIRTUAL_HEIGHT * this.scale,
    );
  }
}
